import {
  useEffect,
  useMemo,
  useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Alert
} from '@mui/material';
import Autocomplete from '../components/SearchableAutocomplete';
import Select from '../components/SearchableSelect';
import { getAllStanze } from '../APIs/RoomsAPI';
import { creaTavolo, getAllTavoli, multiCreaTavolo, updateCompatibleSetups } from '../APIs/TablesAPI';
import { getAllSetups } from '../APIs/SetupsAPI';
import { useTranslation } from 'react-i18next';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import { useSnackbar } from '../contexts/SnackBarContext';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

const ModalTavolo = ({ open, onClose, onSuccess }) => {
  const [nome, setNome] = useState('');
  const [nome_prefix, setNome_prefix] = useState('');
  const [nome_suffix, setNome_suffix] = useState('');
  const [note, setNote] = useState('');
  const [roomId, setRoomId] = useState('');
  const [maxPosti, setMaxPosti] = useState(0);
  const [tableQuantity, setTableQuantity] = useState(0);
  const [stanze, setStanze] = useState([]);
  const [tavoli, setTavoli] = useState([]);
  const [setups, setSetups] = useState([]);
  const [parentTableId, setParentTableId] = useState('');
  const [compatibleSetupIds, setCompatibleSetupIds] = useState([]);
  const [errTavoli, setErrTavoli] = useState(false)
  const [saveError, setSaveError] = useState('');
  const [creazioneMultipla, setCreazioneMultipla] = useState(false)

  const { t } = useTranslation()
  const tenantType = localStorage.getItem('tenantType');
  const isConferenceTenant = String(tenantType || '').toUpperCase() === 'SALE_CONGRESSI';
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      const token = localStorage.getItem('accessToken');
      const tenant = localStorage.getItem('tenantId');
      getAllStanze(token, tenant, { noCache: true }).then(data => setStanze(data)).catch(err => console.error(err));
      if (isConferenceTenant) {
        getAllTavoli(token, tenant, { noCache: true }).then(data => setTavoli(data)).catch(err => console.error(err));
        getAllSetups(token, tenant).then(data => setSetups(data)).catch(err => console.error(err));
      }
    }
  }, [open, isConferenceTenant]);

  const nome_completo = useMemo(() => {
    if (creazioneMultipla)
      return (nome_prefix + " " + nome + " " + nome_suffix);
    else return nome
  }, [nome, nome_prefix, nome_suffix, creazioneMultipla]);
  const stanzeAttive = useMemo(() => stanze.filter((stanza) => stanza?.active !== false), [stanze]);

  const handleSubmit = async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const maxSeats = Number(maxPosti)

    const quantity = parseInt(tableQuantity)
    setErrTavoli(false)
    setSaveError('');

    if (!nome || !roomId) return;
    if (!Number.isInteger(maxSeats) || maxSeats < 1) {
      setErrTavoli(true);
      return;
    }

    try {
      if (!creazioneMultipla) {
        const created = await creaTavolo({ name: nome, note, maxSeats, roomId, parentTableId: parentTableId || null }, token, tenant);
        if (isConferenceTenant && compatibleSetupIds.length > 0 && created?.id) {
          await updateCompatibleSetups(created.id, compatibleSetupIds, token, tenant);
        }
        window.dispatchEvent(new Event('map-data-changed'));
        if (onSuccess) onSuccess();
        handleClose()
      }
      else if (creazioneMultipla) {
        const created = await multiCreaTavolo({ name: nome_completo.trim(), note, maxSeats, roomId, quantity, parentTableId: parentTableId || null }, token, tenant);
        if (isConferenceTenant && compatibleSetupIds.length > 0 && Array.isArray(created?.tables)) {
          await Promise.all(created.tables.map((table) => updateCompatibleSetups(table.id, compatibleSetupIds, token, tenant)));
        }
        window.dispatchEvent(new Event('map-data-changed'));
        if (onSuccess) onSuccess();
        handleClose()
      } else {
        throw new Error("Errore creazione multipla dei tavoli")
      }
    } catch (err) {
      setSaveError(localizedErrorMessage(err, t('reservations_details.error')));
      console.error('Errore creazione tavolo:', err);
      handleAlert(t("modals_alert.error_createTable") + err, "error");
    }
  };

  const handleClose = () => {
    setNome('');
    setNome_prefix('');
    setNome_suffix('');
    setNote('');
    setRoomId('');
    setMaxPosti(0);
    setTableQuantity(0);
    setParentTableId('');
    setCompatibleSetupIds([]);
    setErrTavoli(false)
    setSaveError('');
    onClose();
  };

  const toggleLabel = creazioneMultipla
    ? t("singleCreate", { defaultValue: t("add_table") })
    : t("bulkCreate", { defaultValue: t("table_quantity") });

  function handleAlert(msg, type) {
    showSnackbar(msg, type)
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display={"flex"} justifyContent={'space-between'}>
          <Typography variant='h5'>{getTenantTranslation(t, tenantType, 'addTable')}</Typography>
          <Button variant='contained' onClick={() => { setCreazioneMultipla(!creazioneMultipla) }}>
            {toggleLabel}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box fullWidth display={'flex'} justifyContent={'space-between'}>
          {creazioneMultipla &&
            <TextField
              fullWidth
              margin="normal"
              label={getTenantTranslation(t, tenantType, 'tablePrefix')}
              value={nome_prefix}
              onChange={(e) => setNome_prefix(e.target.value)}
              sx={{ mr: 1 }}
            />}
          <TextField
            fullWidth
            margin="normal"
            label={getTenantTranslation(t, tenantType, 'tableName')}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
          {creazioneMultipla &&
            <TextField
              fullWidth
              margin="normal"
              label={getTenantTranslation(t, tenantType, 'tableSuffix')}
              value={nome_suffix}
              onChange={(e) => setNome_suffix(e.target.value)}
              sx={{ ml: 1 }}
            />}
        </Box>
        {creazioneMultipla &&
          <TextField
            fullWidth
            type='number'
            margin="normal"
            label={getTenantTranslation(t, tenantType, 'tableQuantity')}
            value={parseInt(tableQuantity)}
            onChange={(e) => setTableQuantity(e.target.value)}
          />
        }
        <FormControl fullWidth margin="normal">
          <InputLabel id="room-label">{getTenantTranslation(t, tenantType, 'room')}</InputLabel>
          <Select
            labelId="room-label"
            value={roomId}
            label={getTenantTranslation(t, tenantType, 'room')}
            onChange={(e) => setRoomId(e.target.value)}
          >
            {stanzeAttive.map((stanza) => (
              <MenuItem key={stanza.id} value={stanza.id}>
                {stanza.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          type='number'
          margin="normal"
          label={getTenantTranslation(t, tenantType, 'seats')}
          value={maxPosti}
          onChange={(e) => { setMaxPosti(e.target.value); setErrTavoli(false); }}
          error={errTavoli}
          helperText={errTavoli ? getTenantTranslation(t, tenantType, 'error_number_of_seats') : ''}
          inputProps={{ min: 1, step: 1 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          label={t('notes')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 1000 }}
        />
        {isConferenceTenant && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="parent-table-label">{t("parentRoomOptional")}</InputLabel>
              <Select
                labelId="parent-table-label"
                value={parentTableId}
                label={t("parentRoomOptional")}
                onChange={(e) => setParentTableId(e.target.value)}
              >
                <MenuItem value="">{t("noParentRoom")}</MenuItem>
                {tavoli.map((tavolo) => (
                  <MenuItem key={tavolo.id} value={tavolo.id}>
                    {tavolo.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                {t("parentRoomHelp")}
              </Typography>
            </FormControl>
            <Autocomplete
              multiple
              options={setups.filter((setup) => setup.active !== false)}
              value={setups.filter((setup) => compatibleSetupIds.includes(setup.id))}
              onChange={(_, value) => setCompatibleSetupIds(value.map((item) => item.id))}
              getOptionLabel={(option) => option?.name || ""}
              renderInput={(params) => <TextField {...params} margin="normal" label={t("Allestimenticompatibili")} />}
            />
            <Typography variant="caption" color="text.secondary">
              {t("durante")}
            </Typography>
          </>
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!nome_completo || !roomId}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalTavolo;
